import { useContext, useEffect, useRef } from 'react';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import { useNavigate } from 'react-router';
import EmptyMessage from 'components/Dashboard/components/EmptyMessage';
import useAddMetaTags from 'components/Dashboard/utilities/addMetaTags';
import OutsideFilters from 'components/Dashboard/Filters/ui/OutsideFilters';
import useGetSectionFilters from 'components/Dashboard/Filters/useGetSectionFilters';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import { store } from 'components/Store';
import { getUrlParamRaw, decodeUrlQueryValue } from 'components/Dashboard/utilities/getUrlParam';
import InteractionCard, { InteractionListSkeleton, InteractionScrollSkeleton } from './InteractionCard';
import useCrmInteractionsQuery from './useCrmInteractionsQuery';
import { useInteractionsList } from './InteractionsListContext';

const CrmInteractions = () => {
  const { addMetaTags } = useAddMetaTags();
  const navigate = useNavigate();
  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { interactionLogged, interactionsLocalUpdate, team } = state;

  const { getSectionFilters } = useGetSectionFilters('CrmInteractions');

  const sectionFilters = getSectionFilters();
  const { hash, filters: compiledFilters } = sectionFilters ?? {};

  const searchQuery = getUrlParamRaw('search') ? decodeUrlQueryValue(getUrlParamRaw('search')) : '';

  // Interactions are scoped server-side to the active team, so switching agency clients has to
  // trigger a fresh fetch too - the filter hash/search query alone don't change when only the
  // active team does, which otherwise left the previous client's (now mis-scoped) list on screen
  // until a full page reload forced a refetch.
  const dependencyKey = `${hash}-${searchQuery}-${team?.teamId}`;

  const { interactionsListCache, setInteractionsListCache, scrollPositionRef } = useInteractionsList();
  // Read once at mount: a saved position means we got here via back navigation, so the cached
  // pages should render as-is instead of triggering a fresh fetch.
  const skipInitialFetchRef = useRef(scrollPositionRef.current != null);

  const {
    interactions,
    isLoading,
    isLoadingScroll,
    thereIsMoreContent,
    handleScrollBottom,
    updateInteractionLocal,
    refetch,
  } = useCrmInteractionsQuery({
    filters: compiledFilters,
    sort: { field: 'createdAtUtc', order: 'DESC' },
    query: searchQuery,
    dependencyKey,
    cachedResult: interactionsListCache,
    skipInitialFetch: skipInitialFetchRef.current,
    onResultChange: (result) => setInteractionsListCache(result),
  });

  useEffect(() => {
    if (interactionLogged) {
      refetch();
      dispatch({ type: 'MODIFY_SECTION', parameter: 'interactionLogged', value: false });
    }
  }, [interactionLogged]);

  useEffect(() => {
    if (interactionsLocalUpdate?.length) {
      interactionsLocalUpdate.forEach((item) => updateInteractionLocal(item));
      dispatch({ type: 'MODIFY_SECTION', parameter: 'interactionsLocalUpdate', value: null });
    }
  }, [interactionsLocalUpdate]);

  useEffect(() => {
    addMetaTags({ title: 'Interactions - People - PolicyMogul', hash: null });
    // Selecting interactions here shows a "Delete interaction(s)" action in the shared selection
    // bar - leaving that selected on navigating away would keep it showing on unrelated list
    // pages (e.g. Monitor inbox), which doesn't make sense there.
    return () => {
      dispatch({ type: 'MODIFY_SECTION', parameter: 'selectedUpdates', value: [] });
      dispatch({ type: 'MODIFY_SECTION', parameter: 'selectedUpdatesIds', value: [] });
    };
  }, []);

  useEffect(() => {
    navigate({ search: window.location.search, hash: hash ? `#${hash}` : '' }, { replace: true });
  }, [hash]);

  const filteredInteractions = interactions ?? [];

  return (
    <div className='main-content pr-xl-5 px-md-4 px-3'>
      <div className='row mx-0'>
        <div className='flex-grow-1 reference-main-content'>
          <InteractionListContainer
            isLoading={isLoading}
            interactions={interactions === null ? null : filteredInteractions}
            thereIsMoreContent={thereIsMoreContent}
            isLoadingScroll={isLoadingScroll}
            onScrollBottom={handleScrollBottom}
            dependencyKey={dependencyKey}
            scrollPositionRef={scrollPositionRef}
          />
        </div>
        <div className='ml-4 outside-filters-container'>
          <OutsideFilters section='CrmInteractions' />
        </div>
      </div>
    </div>
  );
};

const InteractionListContainer = ({
  isLoading,
  interactions,
  thereIsMoreContent,
  onScrollBottom,
  dependencyKey,
  scrollPositionRef,
}) => {
  const [heightContainer, containerRef] = useHeightContainer();
  const scrollbarRef = useRef(null);

  useEffect(() => {
    if (scrollPositionRef.current == null) scrollbarRef.current?.scrollToTop();
  }, [dependencyKey]);

  useEffect(() => {
    if (isLoading || scrollPositionRef.current == null || !interactions?.length) return;
    // heightContainer/CustomScrollbar's own measurements can still be settling on the frame this
    // fires, so a single scrollTop() call can land before the container is actually scrollable -
    // retry across a few frames until it sticks.
    let cancelled = false;
    let attempts = 0;
    const tryScroll = () => {
      if (cancelled || scrollPositionRef.current == null) return;
      scrollbarRef.current?.scrollTop(scrollPositionRef.current);
      if (scrollbarRef.current?.getScrollTop() === scrollPositionRef.current) {
        scrollPositionRef.current = null;
        return;
      }
      attempts += 1;
      // Give up gracefully rather than leaving a stale target that could confuse a later restore.
      if (attempts < 10) requestAnimationFrame(tryScroll);
      else scrollPositionRef.current = null;
    };
    requestAnimationFrame(tryScroll);
    return () => {
      cancelled = true;
    };
  }, [isLoading, heightContainer, interactions?.length]);

  const handleScroll = ({ scrollTop, scrollHeight, clientHeight }) => {
    const scrolledToBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight - 1;
    if (scrolledToBottom) {
      onScrollBottom();
    }
  };

  return (
    <div ref={containerRef}>
      <CustomScrollbar
        ref={scrollbarRef}
        className='main-content-scrollbar stakeholder-list-scrollbar'
        style={{ height: `${heightContainer}px` }}
        maximalThumbYSize={100}
        onScroll={handleScroll}
      >
        {isLoading || interactions === null ? (
          <InteractionListSkeleton />
        ) : interactions.length > 0 ? (
          <ul className='policy-list px-0 items-list'>
            {interactions.map((item) => (
              <InteractionCard
                key={`interaction-${item.id}`}
                item={item}
                onNavigate={() => {
                  scrollPositionRef.current = scrollbarRef.current?.getScrollTop() ?? 0;
                }}
              />
            ))}
            {thereIsMoreContent && <InteractionScrollSkeleton />}
          </ul>
        ) : (
          <EmptyMessage resourceName='interactions' clearContentFilters={true} />
        )}
      </CustomScrollbar>
    </div>
  );
};

export default CrmInteractions;
